import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  PlugZap,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { ConnectorStatusBadge } from '@/features/connectors/components/ConnectorStatusBadge';
import {
  useConnector,
  useConnectorDatasets,
  useDeleteConnector,
  useSyncConnector,
  useTestConnector,
} from '@/features/connectors/hooks';
import type { ApiError } from '@/lib/errors';

export function ConnectorDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const connector = useConnector(id);
  const datasets = useConnectorDatasets(id);
  const test = useTestConnector();
  const sync = useSyncConnector();
  const remove = useDeleteConnector();

  if (connector.isPending) {
    return <Spinner label="Loading connector…" />;
  }
  if (connector.isError) {
    return <ErrorState error={connector.error as ApiError} />;
  }

  const item = connector.data;
  const isDatabase = item.type === 'database';

  async function handleDelete(): Promise<void> {
    await remove.mutateAsync(id).catch(() => null);
    navigate('/connectors');
  }

  return (
    <div className="space-y-7">
      <Link
        to="/connectors"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 dark:text-ink-400 transition hover:text-brand-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to data sources
      </Link>

      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow eyebrow-dot mb-2">
            <span>{item.type.toUpperCase()} CONNECTOR</span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="h-display truncate">{item.name}</h1>
            <ConnectorStatusBadge status={item.status} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDatabase && (
            <>
              <button
                type="button"
                disabled={test.isPending}
                onClick={() => test.mutate(id)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-3 text-xs font-medium text-ink-700 dark:text-ink-200 shadow-soft transition hover:-translate-y-px hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {test.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Testing…
                  </>
                ) : (
                  <>
                    <PlugZap className="h-3 w-3" />
                    Test
                  </>
                )}
              </button>
              <button
                type="button"
                disabled={sync.isPending}
                onClick={() => sync.mutate(id)}
                className="btn-primary h-9 px-3 text-xs"
              >
                {sync.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Syncing…
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    Sync schema
                  </>
                )}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-accent-crimson/30 bg-white dark:bg-ink-900 px-3 text-xs font-medium text-accent-crimson transition hover:-translate-y-px hover:bg-accent-crimson/5"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
        </div>
      </div>

      {item.last_error && (
        <div className="flex items-start gap-3 rounded-xl border border-accent-crimson/25 bg-accent-crimson/[0.06] p-4">
          <div className="font-mono text-[10px] tracking-wider text-accent-crimson">LAST ERROR</div>
          <div className="font-mono text-xs text-accent-crimson">{item.last_error}</div>
        </div>
      )}

      {/* Connection details */}
      <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
        <header className="border-b border-ink-100 dark:border-white/5 px-6 py-4">
          <h3 className="h-section">Connection</h3>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">Resolved configuration for this connector.</p>
        </header>
        <dl className="grid grid-cols-1 divide-y divide-ink-100 dark:divide-white/5 md:grid-cols-2 md:divide-y-0 md:[&>div:nth-child(even)]:border-l md:[&>div]:border-ink-100">
          {Object.entries(item.config).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center justify-between gap-6 px-6 py-3"
            >
              <dt className="font-mono text-[10px] font-semibold tracking-[0.18em] text-ink-500 dark:text-ink-400">
                {key.replace(/_/g, ' ').toUpperCase()}
              </dt>
              <dd className="truncate font-mono text-xs text-ink-800 dark:text-ink-100">
                {String(value ?? '—')}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Datasets */}
      <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
        <header className="flex items-start justify-between gap-4 border-b border-ink-100 dark:border-white/5 px-6 py-4">
          <div>
            <h3 className="h-section">Datasets</h3>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
              Tables and sheets produced by this connector.
            </p>
          </div>
          {datasets.data && (
            <span className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
              {datasets.data.length} TOTAL
            </span>
          )}
        </header>
        <div className="p-6">
          {datasets.isPending ? (
            <Spinner />
          ) : datasets.isError ? (
            <ErrorState error={datasets.error as ApiError} />
          ) : datasets.data.length === 0 ? (
            <div className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-ink-50/40 dark:bg-white/[0.02] p-6 text-center text-sm text-ink-500 dark:text-ink-400">
              {isDatabase ? 'Run “Sync schema” to discover tables.' : 'No datasets.'}
            </div>
          ) : (
            <ul className="space-y-2">
              {datasets.data.map((dataset) => (
                <li
                  key={dataset.id}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-ink-200/70 dark:border-white/10 px-4 py-3 transition hover:-translate-y-px hover:border-ink-300 hover:bg-brand-50/30"
                >
                  <div className="min-w-0">
                    <p className="truncate font-mono text-[13px] font-semibold text-ink-900 dark:text-ink-50">
                      {dataset.name}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500 dark:text-ink-400">
                      <span className="tabular-nums font-medium text-ink-700 dark:text-ink-200">
                        {dataset.column_count}
                      </span>{' '}
                      column(s)
                      {dataset.row_count !== null && (
                        <>
                          {' '}
                          ·{' '}
                          <span className="tabular-nums font-medium text-ink-700 dark:text-ink-200">
                            {dataset.row_count}
                          </span>{' '}
                          rows
                        </>
                      )}
                      {dataset.om_fqn && (
                        <>
                          {' '}
                          ·{' '}
                          <span className="font-mono text-[10px] tracking-wider text-brand-700">
                            IN CATALOGUE
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <Link
                    to={`/datasets/${dataset.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700"
                  >
                    View
                    <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
